import {
    UPDATE_ACCESS_LOG,
    APPEND_ACCESS_LOG,
    MERGE_ACCESS_LOG,
    FETCH_MORE_LOGS,
    ACCESS_LOG_ERROR,
    FETCH_ACCESS_LOG_LPR_DOC,
    FETCH_ACCESS_LOG_LPR_DOC_SUCCESS,
    FETCH_ACCESS_LOG_LPR_DOC_ERROR,
    CLEAR_ACCESS_LOG_LPR_DOC,
    FETCH_ACCESS_LOG_DEVICE,
    FETCH_ACCESS_LOG_DEVICE_SUCCESS,
    FETCH_ACCESS_LOG_DEVICE_ERROR,
    CLEAR_ACCESS_LOG_DEVICE,
    FETCH_ACCESS_LOG_ACCESS_KEY,
    FETCH_ACCESS_LOG_ACCESS_KEY_SUCCESS,
    FETCH_ACCESS_LOG_ACCESS_KEY_ERROR,
    CLEAR_ACCESS_LOG_ACCESS_KEY,
    FETCH_ACCESS_LOG_GUARD_IDENTITY,
    FETCH_ACCESS_LOG_GUARD_IDENTITY_SUCCESS,
    FETCH_ACCESS_LOG_GUARD_IDENTITY_ERROR,
    CLEAR_ACCESS_LOG_GUARD_IDENTITY,
    FETCH_ACCESS_LOG_APPROVER_INVITE,
    FETCH_ACCESS_LOG_APPROVER_INVITE_SUCCESS,
    FETCH_ACCESS_LOG_APPROVER_INVITE_ERROR,
    CLEAR_ACCESS_LOG_APPROVER_INVITE,
    SET_ACTIVE_USER_ORG
} from 'store/actions/types';

const LIVE_LIMIT = 100;

const initialState = {
    accessLog: [],
    hasMore: false,
    loading: false,
    error: null,
    lastCursor: null,
    accessLogLpr: null,
    accessLogLprLoading: false,
    accessLogLprError: null,
    accessLogDevice: null,
    accessLogDeviceLoading: false,
    accessLogDeviceError: null,
    accessLogAccessKey: null,
    accessLogAccessKeyLoading: false,
    accessLogAccessKeyError: null,
    guardIdentity: null,
    guardIdentities: {},
    guardIdentityLoading: false,
    guardIdentityError: null,
    approverInvite: null,
    approverInvites: {},
    approverInviteLoading: false,
    approverInviteError: null
};

export default function accessReducer(state = initialState, action) {
    switch (action.type) {
        case FETCH_MORE_LOGS: {
            // console.log('[AL][reducer] FETCH_MORE_LOGS -> loading:true', action.payload);
            return {
                ...state,
                loading: true,
                error: null
            };
        }

        case ACCESS_LOG_ERROR: {
            // console.log(
            //     '[AL][reducer] ACCESS_LOG_ERROR -> loading:false',
            //     action.payload
            // );
            return {
                ...state,
                loading: false,
                error: action.payload
            };
        }

        case UPDATE_ACCESS_LOG: {
            const newLogs = action.payload || [];
            // console.log(
            //     '[AL][reducer] UPDATE_ACCESS_LOG -> loading:false, count:',
            //     newLogs.length
            // );
            const last = newLogs.length ? newLogs[newLogs.length - 1] : null;
            return {
                ...state,
                accessLog: newLogs,
                loading: false,
                error: null,
                hasMore: newLogs.length >= LIVE_LIMIT,
                lastCursor: last?.attempted_at ?? null
            };
        }

        case APPEND_ACCESS_LOG: {
            const moreLogs = action.payload || [];
            const combined = [...state.accessLog, ...moreLogs];
            const last = moreLogs.length ? moreLogs[moreLogs.length - 1] : null;
            return {
                ...state,
                loading: false,
                accessLog: combined,
                hasMore: moreLogs.length >= LIVE_LIMIT,
                lastCursor: last?.attempted_at ?? state.lastCursor
            };
        }

        //
        // Upserts the live window into whatever is already held. Access logs used to be
        // write-once, so appending new ids was enough; vehicle capture and access clips
        // patch a log after the fact, and those updates have to land too.
        //
        // Three things this must not do, because the list's IntersectionObserver is
        // rebuilt whenever `lastCursor` or `hasMore` changes and re-fires immediately
        // while the sentinel is on screen — touching either from a live snapshot starts
        // a pagination runaway:
        //   1. write anything but `accessLog`
        //   2. allocate a new state object when nothing actually differed
        //   3. drop rows the window no longer covers — they belong to the paginated tail
        //
        case MERGE_ACCESS_LOG: {
            const incoming = action.payload || [];
            if (!incoming.length) return state;

            const positions = new Map(state.accessLog.map((log, i) => [log.logItemId, i]));
            let next = null;

            incoming.forEach(log => {
                const at = positions.get(log.logItemId);

                if (at === undefined) {
                    next = next || [...state.accessLog];
                    next.push(log);
                    return;
                }

                // Every snapshot hands back fresh objects, so identity says nothing —
                // compare by value, and only when Firestore reported a change at all.
                const current = (next || state.accessLog)[at];
                if (JSON.stringify(current) === JSON.stringify(log)) return;

                next = next || [...state.accessLog];
                next[at] = log;
            });

            if (!next) return state;

            next.sort((a, b) => {
                const bs = b.attempted_at?.seconds ?? 0;
                const as = a.attempted_at?.seconds ?? 0;
                return bs !== as
                    ? bs - as
                    : (b.attempted_at?.nanoseconds ?? 0) - (a.attempted_at?.nanoseconds ?? 0);
            });

            return { ...state, accessLog: next };
        }

        case FETCH_ACCESS_LOG_LPR_DOC:
            return {
                ...state,
                accessLogLpr: null,
                accessLogLprLoading: true,
                accessLogLprError: null
            };

        case FETCH_ACCESS_LOG_LPR_DOC_SUCCESS:
            return {
                ...state,
                accessLogLpr: action.payload,
                accessLogLprLoading: false,
                accessLogLprError: null
            };

        case FETCH_ACCESS_LOG_LPR_DOC_ERROR:
            return {
                ...state,
                accessLogLpr: null,
                accessLogLprLoading: false,
                accessLogLprError: action.payload
            };

        case CLEAR_ACCESS_LOG_LPR_DOC:
            return {
                ...state,
                accessLogLpr: null,
                accessLogLprLoading: false,
                accessLogLprError: null
            };
        case FETCH_ACCESS_LOG_DEVICE:
            return {
                ...state,
                accessLogDevice: null,
                accessLogDeviceLoading: true,
                accessLogDeviceError: null
            };

        case FETCH_ACCESS_LOG_DEVICE_SUCCESS:
            return {
                ...state,
                accessLogDevice: action.payload,
                accessLogDeviceLoading: false,
                accessLogDeviceError: null
            };

        case FETCH_ACCESS_LOG_DEVICE_ERROR:
            return {
                ...state,
                accessLogDevice: null,
                accessLogDeviceLoading: false,
                accessLogDeviceError: action.payload
            };

        case CLEAR_ACCESS_LOG_DEVICE:
            return {
                ...state,
                accessLogDevice: null,
                accessLogDeviceLoading: false,
                accessLogDeviceError: null
            };

        case FETCH_ACCESS_LOG_ACCESS_KEY:
            return {
                ...state,
                accessLogAccessKey: null,
                accessLogAccessKeyLoading: true,
                accessLogAccessKeyError: null
            };

        case FETCH_ACCESS_LOG_ACCESS_KEY_SUCCESS:
            return {
                ...state,
                accessLogAccessKey: action.payload,
                accessLogAccessKeyLoading: false,
                accessLogAccessKeyError: null
            };

        case FETCH_ACCESS_LOG_ACCESS_KEY_ERROR:
            return {
                ...state,
                accessLogAccessKey: null,
                accessLogAccessKeyLoading: false,
                accessLogAccessKeyError: action.payload
            };

        case CLEAR_ACCESS_LOG_ACCESS_KEY:
            return {
                ...state,
                accessLogAccessKey: null,
                accessLogAccessKeyLoading: false,
                accessLogAccessKeyError: null
            };

        case FETCH_ACCESS_LOG_GUARD_IDENTITY:
            const cashedIdentity = state.guardIdentities[action.payload] || null;
            return {
                ...state,
                guardIdentity: cashedIdentity,
                guardIdentityLoading: !cashedIdentity,
                guardIdentityError: null
            };

        case FETCH_ACCESS_LOG_GUARD_IDENTITY_SUCCESS:
            return {
                ...state,
                guardIdentity: action.payload,
                guardIdentities: action.payload?.uid ? {
                    ...state.guardIdentities,
                    [action.payload.uid]: action.payload
                } : state.guardIdentities,
                guardIdentityLoading: false,
                guardIdentityError: null
            };

        case FETCH_ACCESS_LOG_GUARD_IDENTITY_ERROR:
            return {
                ...state,
                guardIdentity: null,
                guardIdentityLoading: false,
                guardIdentityError: action.payload
            };

        case CLEAR_ACCESS_LOG_GUARD_IDENTITY:
            return {
                ...state,
                guardIdentity: null,
                guardIdentityLoading: false,
                guardIdentityError: null
            };

        case FETCH_ACCESS_LOG_APPROVER_INVITE: {
            const cachedApproverInvite = state.approverInvites[action.payload] || null;
            return {
                ...state,
                approverInvite: cachedApproverInvite,
                approverInviteLoading: !cachedApproverInvite,
                approverInviteError: null
            };
        }

        case FETCH_ACCESS_LOG_APPROVER_INVITE_SUCCESS:
            return {
                ...state,
                approverInvite: action.payload,
                approverInvites: action.payload?.invite_id
                    ? {
                          ...state.approverInvites,
                          [action.payload.invite_id]: action.payload
                      }
                    : state.approverInvites,
                approverInviteLoading: false,
                approverInviteError: null
            };

        case FETCH_ACCESS_LOG_APPROVER_INVITE_ERROR:
            return {
                ...state,
                approverInvite: null,
                approverInviteLoading: false,
                approverInviteError: action.payload
            };

        case CLEAR_ACCESS_LOG_APPROVER_INVITE:
            return {
                ...state,
                approverInvite: null,
                approverInviteLoading: false,
                approverInviteError: null
            };

        case SET_ACTIVE_USER_ORG:
            return {
                ...state,
                guardIdentity: null,
                guardIdentities: {},
                guardIdentityLoading: false,
                guardIdentityError: null,
                approverInvite: null,
                approverInvites: {},
                approverInviteLoading: false,
                approverInviteError: null
            };    
        default:
            return state;
    }
}
